<template>
	<div
		:class="mapSize ? mapSize : 'map-sm'"
		ref="map"
		style="width:100%;"
	></div>
</template>

<script>
import GoogleMapsLoader from "@/mixins/GoogleMapsLoader"

export default {
	name: "VueMap",
	mixins: [GoogleMapsLoader],
	props: {
		center: Object,
		zoom: Number,
		mapSize: String,
		markers: {
			type: Array,
			default: () => []
		},
		circles: {
			type: Array,
			default: () => []
		},
		/**
		* array of lines ex. [
		*	{ trail: [{lat,lng}, ...], strokeColor: 'red' },
		*	...
		* ]
		**/
		lines: {
			type: Array,
			default: () => []
		}
	},

	data() {
		return {
			map: null,
			mapsRef: null,
			infoWindow: null,
			markerObjects: [],
			circleObjects: [],
			lineObjects: []
		};
	},

	async mounted() {
		const maps = await this.loadGoogleMaps(
			this.$store.GoogleEmbedAPIkey
		);

		this.mapsRef = maps;

		this.map = new maps.Map(this.$refs.map, {
			center: this.center,
			zoom: this.zoom
		});

		this.infoWindow = new maps.InfoWindow();

		this.renderMarkers();
		this.renderCircles();
		this.renderLines();
	},

	methods: {

		linePointLatLng(point) {
			return {
				lat: +(point.lat != null ? point.lat : point.latitude),
				lng: +(point.lng != null ? point.lng : point.longitude)
			}
		},

		renderMarkers() {
			if (!this.mapsRef) return;

			this.markerObjects.forEach(marker => marker.setMap(null));
			this.markerObjects = [];

			this.markers.forEach(marker => {
				const m = new this.mapsRef.Marker({
					map: this.map,
					position: { lat: marker.lat, lng: marker.lng },
					draggable: marker.draggable ? marker.draggable : false,
					icon: marker.icon || null
				});

				m.addListener("click", () => {
					if (this.infoWindow && marker && marker.infoWindowText) {
						this.infoWindow.setContent(marker.infoWindowText);
						this.infoWindow.open(this.map, m);
					}
					this.$emit("markerClick", marker);
				});

				m.addListener("dragend", e => {
					this.$emit("markerDrop", {
						lat: e.latLng.lat(),
						lng: e.latLng.lng()
					}, marker);
				});

				this.markerObjects.push(m);
			});
		},

		renderCircles() {
			if (!this.mapsRef) return;

			this.circleObjects.forEach(circle => circle.setMap(null));
			this.circleObjects = [];

			this.circles.forEach(circle => {
				const c = new this.mapsRef.Circle({
					map: this.map,
					center: { lat: circle.lat, lng: circle.lng },
					radius: circle.radius,
					strokeColor: circle.strokeColor,
					fillColor: circle.fillColor
				});

				this.circleObjects.push(c);
			});
		},

		renderLines() {
			if (!this.mapsRef || !this.map) return;

			this.lineObjects.forEach(line => line.setMap(null));
			this.lineObjects = [];

			if (!this.lines || !this.lines.length)
				return;

			this.lines.forEach((line, index) => {
				if (!line.trail || line.trail.length < 2)
					return;

				const path = line.trail.map(point => this.linePointLatLng(point));

				const polyline = new this.mapsRef.Polyline({
					map: this.map,
					path,
					strokeColor: line.strokeColor || "darkgreen",
					strokeOpacity: 1,
					strokeWeight: 4
				});

				polyline.addListener("click", () => {
					this.lineClick(index);
				});

				this.lineObjects.push(polyline);
			});
		},

		lineClick(index) {
			if (!this.infoWindow || !this.lines[index])
				return;

			let theLine = this.lines[index]

			let midIndex = 0
			if (theLine.trail.length > 2)
				midIndex = Math.floor(theLine.trail.length / 2)

			let midPoint = this.linePointLatLng(theLine.trail[midIndex])

			let maxSpeedReached = 0
			theLine.trail.forEach(point => {
				if (point.speed > maxSpeedReached)
					maxSpeedReached = point.speed
			})

			let infoWindowText = "Speed: " + maxSpeedReached + "km/h"
			if ("maxSpeed" in theLine.trail[1] && theLine.trail[1].maxSpeed > 0)
				infoWindowText += "\nSpeed Limit: " + theLine.trail[1].maxSpeed + " km/h"

			this.infoWindow.setContent(infoWindowText.replace("\n", "<br>"))
			this.infoWindow.setPosition(midPoint)
			this.infoWindow.open(this.map)
		}
	},

	watch: {
		markers() {
			if (this.map && this.mapsRef) this.renderMarkers();
		},
		circles() {
			if (this.map && this.mapsRef) this.renderCircles();
		},
		lines() {
			if (this.map && this.mapsRef) this.renderLines();
		}
	}
};
</script>

<style scoped>
	.map-sm { height: 300px; max-height: calc(100vh - 120px); }
	.map-md { height: 500px; max-height: calc(100vh - 120px); }
	.map-lg { height: 700px; max-height: calc(100vh - 120px); }
	.map-inherit { height: 100%; }
</style>
